// Graph ↔ JSON serialization (ARCHITECTURE.md §3, src/graph).
//
// A flat, JSON-safe representation of an AdjacencyListGraph: nodes and edges as
// parallel arrays. This is the on-disk / over-the-wire shape used by the
// persistent cache and the CLI. Deserialization re-validates through the Zod
// schemas because it sits at a trust boundary (cache files, plugin input).

import { z } from "zod";

import { AdjacencyListGraph } from "./adjacency.js";
import {
  GraphEdgeSchema,
  GraphNodeSchema,
  type GraphEdge,
  type GraphNode,
} from "../types.js";

export const SerializedGraphSchema = z.object({
  nodes: z.array(GraphNodeSchema),
  edges: z.array(GraphEdgeSchema),
});

export type SerializedGraph = z.infer<typeof SerializedGraphSchema>;

/**
 * Flatten a graph into a JSON-compatible object. Nodes and edges are emitted
 * in insertion order, so the output is stable for a given construction
 * sequence.
 */
export function serializeGraph(graph: AdjacencyListGraph): SerializedGraph {
  return {
    nodes: graph.getNodes(),
    edges: graph.getAllEdges(),
  };
}

/**
 * Rebuild an AdjacencyListGraph from its serialized form. Input is validated
 * through the Zod schemas; malformed data throws a ZodError. Nodes are added
 * before edges so that endpoint nodes are present, but edges referencing
 * unknown nodes are still accepted (mirroring addEdge's tolerance).
 */
export function deserializeGraph(data: unknown): AdjacencyListGraph {
  const parsed = SerializedGraphSchema.parse(data);
  const graph = new AdjacencyListGraph();

  for (const node of parsed.nodes as GraphNode[]) {
    graph.addNode(node);
  }
  for (const edge of parsed.edges as GraphEdge[]) {
    graph.addEdge(edge);
  }

  return graph;
}
